import React, { useMemo } from 'react';
import { useFetchEmailTemplates } from '../../../query/template';
import TablePagination from '../../../components/ui/TablePagination';
import ReactTable from 'react-table';
import UserWithAvatar from '../../../components/ui/UserWithAvatar';
import { Tag, Tooltip } from 'antd';
import TemplateStatistic from '../../../components/ui/TemplateStatistic';
import ReactivateTemplateButton from './ReactivateTemplateButton';
import EditWebhookButton from './EditWebhookButton';
import DeleteTemplateButton from './DeleteTemplateButton';
import styles from './Templates.module.css';
import NameFiltering from './Filtering/NameFiltering';
import { useTemplateListingStore } from './store';
import CategoryFiltering from './Filtering/CategoryFiltering';
import EmailTypeFiltering from './Filtering/EmailTypeFiltering';
import EditHistoryButton from './EditHistory/EditHistoryButton';
import ViewTemplateMetricsButton from './TemplateMetrics/ViewTemplateMetricsButton';
import SortableHeader from './SortableHeader';
import useCheckPermissions from 'hooks/auth/useCheckPermission';
import EditDailyTemplateQuotaCell from './EditDailyTemplateQuotaCell';

type Props = {
  onEditTemplate: (templateId: number) => void,
  handleAddOrEditTemplate: (template: any) => void,
  handleDeleteTemplate: (templateId: number) => void,
};

export default function TemplateTable({
  onEditTemplate,
  handleAddOrEditTemplate,
  handleDeleteTemplate,
}: Props) {
  const { userCheckPermission } = useCheckPermissions();
  const isAdmin = userCheckPermission('user.view');
  const canReactivateTemplate = userCheckPermission('mail.template.activate');
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    setSortColumn,
  } = useTemplateListingStore();
  const [{ data = [], isFetching }, queryFilter] = useFetchEmailTemplates();
  const [rows = [], count = 0] = data;
  const columnDefs = useMemo(() => {
    return [
      {
        Header: <SortableHeader accessor="id">ID</SortableHeader>,
        accessor: 'id',
        maxWidth: 60,
      },
      {
        Header: (
          <SortableHeader accessor="name">
            <p style={{ marginBottom: 5 }}>Name</p>
            <div onClick={e => e.stopPropagation()}>
              <NameFiltering />
            </div>
          </SortableHeader>
        ),
        id: 'name',
        accessor: template => {
          const { report, status: templateStatus, name, id } = template;
          return (
            <div className="flex space-x-4" style={{ alignItems: 'center' }}>
              <div className="flex space-x-1" style={{ alignItems: 'center' }}>
                <ViewTemplateMetricsButton template={template} />
                {templateStatus === 'INACTIVE' && (
                  <Tooltip title="This template is marked as inactive because it hasn't been used / updated for 2 months">
                    <Tag color="orange" style={{ marginRight: 0 }}>
                      Inactive
                    </Tag>
                  </Tooltip>
                )}
                {templateStatus === 'INACTIVE' ? (
                  <p style={{ marginBottom: 0 }}>{name}</p>
                ) : (
                  <a
                    style={{ marginBottom: 0 }}
                    onClick={() => onEditTemplate(id)}
                  >
                    {name}
                  </a>
                )}
              </div>
              <div className="flex space-x-2" style={{ alignItems: 'center' }}>
                {!!report?.numOfErrors && (
                  <TemplateStatistic count={report.numOfErrors} type="error" />
                )}
                {!!report?.numOfWarnings && (
                  <TemplateStatistic
                    count={report.numOfWarnings}
                    type="warning"
                  />
                )}
              </div>
            </div>
          );
        },
      },
      {
        Header: (
          <SortableHeader accessor="category.name">
            <p style={{ marginBottom: 5 }}>Category</p>
            <div onClick={e => e.stopPropagation()}>
              <CategoryFiltering />
            </div>
          </SortableHeader>
        ),
        accessor: 'category.name',
      },
      {
        Header: (
          <SortableHeader accessor="emailType.type">
            <p style={{ marginBottom: 5 }}>Email Type</p>
            <div onClick={e => e.stopPropagation()}>
              <EmailTypeFiltering />
            </div>
          </SortableHeader>
        ),
        accessor: 'emailType.type',
      },
      {
        Header: (
          <SortableHeader accessor="bypassUnsubscription">
            Bypass Unsubscription
          </SortableHeader>
        ),
        id: 'bypassUnsubscription',
        accessor: row => (
          <div style={{ textAlign: 'center' }}>
            <Tag color={row.bypassUnsubscription ? 'red' : 'green'}>
              {row.bypassUnsubscription ? 'Yes' : 'No'}
            </Tag>
          </div>
        ),
      },
      {
        Header: (
          <SortableHeader accessor="totalEmailsSent">Volume</SortableHeader>
        ),
        id: 'totalEmailsSent',
        accessor: 'totalEmailsSent',
        style: { textAlign: 'center' },
        width: 100,
      },
      {
        Header: (
          <SortableHeader accessor="dailyQuota">Daily Quota</SortableHeader>
        ),
        id: 'dailyQuota',
        accessor: isAdmin
          ? row => (
              <EditDailyTemplateQuotaCell
                dailyQuota={row.dailyQuota}
                queryFilter={queryFilter}
                templateId={row.id}
              />
            )
          : 'dailyQuota',
        style: { textAlign: isAdmin ? 'right' : 'center' },
        width: isAdmin ? 150 : 100,
      },
      {
        Header: (
          <SortableHeader accessor="createdBy">Created By</SortableHeader>
        ),
        id: 'createdBy',
        accessor: row => {
          if (!row.createdByUser) {
            return 'N/A';
          }
          return <UserWithAvatar user={row.createdByUser} />;
        },
      },
      {
        Header: (
          <SortableHeader accessor="updatedByUser">Updated By</SortableHeader>
        ),
        id: 'updatedBy',
        accessor: row => {
          if (!row.updatedByUser) {
            return 'N/A';
          }
          return <UserWithAvatar user={row.updatedByUser} />;
        },
      },
      {
        Header: (
          <SortableHeader accessor="updatedAt">Last Updated</SortableHeader>
        ),
        id: 'updatedAt',
        accessor: row => {
          return new Date(row.updatedAt).toLocaleString();
        },
      },
      {
        Header: 'Actions',
        id: 'actions',
        minWidth: 200,
        accessor: row => {
          return (
            <div
              className="flex space-x-2"
              style={{ justifyContent: 'center' }}
            >
              {row.status === 'INACTIVE' && (
                <ReactivateTemplateButton
                  disabled={!canReactivateTemplate}
                  template={row}
                />
              )}
              {row.status !== 'INACTIVE' && (
                <EditWebhookButton
                  template={row}
                  onEdit={handleAddOrEditTemplate}
                />
              )}
              <EditHistoryButton template={row} />
              <DeleteTemplateButton
                template={row}
                onDelete={handleDeleteTemplate}
              />
            </div>
          );
        },
      },
    ];
  }, [handleAddOrEditTemplate, handleDeleteTemplate]);
  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <ReactTable
          PaginationComponent={TablePagination}
          className={`-highlight ${styles.innerTable}`}
          columns={columnDefs}
          data={rows}
          defaultPageSize={15}
          firstText={<i className="material-icons">first_page</i>}
          getTheadProps={() => {
            return {
              onClick: e => {
                const elementWithAccessor = e.target.querySelector(
                  '[data-accessor]',
                );
                const accessor = elementWithAccessor
                  ? elementWithAccessor.getAttribute('data-accessor')
                  : null;
                if (accessor) setSortColumn(accessor);
              },
            };
          }}
          lastText={<i className="material-icons">last_page</i>}
          loading={isFetching}
          manual
          minRows={0}
          nextText={<i className="material-icons">chevron_right</i>}
          page={page - 1}
          pageSize={pageSize}
          pageSizeOptions={[5, 10, 15, 20]}
          pages={parseInt(count / pageSize) + 1}
          previousText={<i className="material-icons">chevron_left</i>}
          rowsSelectorText="Rows per page"
          showPageJump={false}
          showPaginationBottom
          onPageChange={nextPage => setPage(nextPage + 1)}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
}
